import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { formatUsDate } from '../../api/format'
import DateInput from '../../components/ui/DateInput'
import { useRefreshReviewAssignment } from '../../hooks/queries/carerxClinical'
import CarePlanAssignee from './CarePlanAssignee'
import { deidentifiedAiAssist } from '../pot/pcw/pcwAiClient'

/** D-CRX-36 S3 — the structured Care Plan editor (spec §A Stage 6). Draft → edit → sign.
 *  Signed is immutable (server-enforced); "New version" forks. Goals are rows with status chips
 *  (updatable on signed plans — the evidence trail). Cadence tasks complete/skip inline;
 *  annual completion is refused by the server until a newer version is signed. */
export default function CarePlanPanel({ therapyId, drugName, phiValues = [] }: { therapyId: string; drugName?: string | null; phiValues?: string[] }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getCareRxCarePlan>> | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [gText, setGText] = useState(''); const [gHorizon, setGHorizon] = useState('short')
  const [gTarget, setGTarget] = useState(''); const [gDate, setGDate] = useState('')
  const [aiDrafts, setAiDrafts] = useState<{ horizon: string; goal_text: string; measurable_target?: string }[] | null>(null)
  const [aiSummary, setAiSummary] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [doses, setDoses] = useState<{ dose_no: number; dosed_at: string; source: string }[]>([])
  const [doseDate, setDoseDate] = useState('')
  const refreshAssignment = useRefreshReviewAssignment()

  const load = useCallback(() => {
    api.listCareRxDoses(therapyId).then(d => setDoses(d.items ?? [])).catch(() => setDoses([]))
    api.getCareRxCarePlan(therapyId).then(setData).catch((e: any) => {
      setData({ latest: null, versions: [], goals: [], cadence: [] })
      if (e?.body?.error === 'pending_migration') setMsg('Care-plan tables await migrations 259–261 on this pharmacy.')
    })
  }, [therapyId])
  useEffect(() => { load() }, [load])

  const latest = data?.latest
  const isDraft = latest?.status === 'draft'
  const iv = (() => { try { return latest?.interventions ? JSON.parse(latest.interventions) : {} } catch { return {} } })()
  const problems: { code: string; description: string }[] = (() => { try { return latest?.problem_list ? JSON.parse(latest.problem_list) : [] } catch { return [] } })()

  const saveDraft = async (patch: any) => {
    if (!latest || !isDraft) return
    setBusy(true); setMsg('')
    try { await api.updateCareRxCarePlan(latest.id, patch); load() }
    catch (e: any) { setMsg(e?.body?.message || 'Save failed.') } finally { setBusy(false) }
  }

  if (!data) return <div style={{ fontSize: 12.5 }}><Loader2 className="spin" size={13} /> Loading care plan…</div>

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b>Care Plan — {drugName || 'therapy'}</b>
        <span>
          {latest ? <span style={{ marginRight: 10, color: 'var(--color-text-mute)' }}>v{latest.version} · {latest.status}{latest.signed_at ? ` · signed ${formatUsDate(latest.signed_at)}` : ''}</span> : null}
          {(!latest || latest.status !== 'draft') && (
            <button disabled={busy} style={btn} onClick={async () => {
              setBusy(true); setMsg('')
              try { await api.createCareRxCarePlan(therapyId); load() }
              catch (e: any) { setMsg(e?.body?.message || 'Could not create a draft.') } finally { setBusy(false) }
            }}>{latest ? 'New version' : 'Start plan'}</button>
          )}
          {isDraft && (
            <button disabled={busy} style={{ ...btn, background: 'var(--color-primary)', color: '#fff' }} onClick={async () => {
              setBusy(true); setMsg('')
              try {
                const r = await api.signCareRxCarePlan(latest.id)
                setMsg(`Signed — ${r.cadence_spawned} follow-up task(s) scheduled.${r.auto_assigned ? ' You are now its pharmacist.' : ''}`)
                refreshAssignment(therapyId); load()
              }
              catch (e: any) { setMsg(e?.body?.message || 'Sign failed.') } finally { setBusy(false) }
            }}>Sign plan</button>
          )}
        </span>
      </div>
      <CarePlanAssignee therapyId={therapyId} />
      {msg && <div style={{ color: '#b45309' }}>{msg}</div>}

      {latest && (<>
        {/* 1 · Problem list */}
        <div>
          <b>Problems (ICD-10)</b>
          {problems.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>{p.code} — {p.description}</span>
              {isDraft && <button style={xbtn} onClick={() => saveDraft({ problem_list: problems.filter((_, j) => j !== i) })}>✕</button>}
            </div>
          ))}
          {isDraft && (
            <button style={btn} onClick={() => {
              const code = window.prompt('ICD-10 code (e.g. L40.50):')?.trim()
              if (!code || !/^[A-TV-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/i.test(code)) { if (code) setMsg('That does not look like an ICD-10 code.'); return }
              const description = window.prompt('Description:')?.trim() || ''
              saveDraft({ problem_list: [...problems, { code: code.toUpperCase(), description }] })
            }}>+ Problem</button>
          )}
        </div>

        {/* 4 · Interventions */}
        <div>
          <b>Interventions</b>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {(['anaphylaxis_kit', 'loading_dose'] as const).map(k => (
              <label key={k} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <input type="checkbox" disabled={!isDraft || busy} checked={!!iv[k]}
                  onChange={e => saveDraft({ interventions: { ...iv, [k]: e.target.checked } })} />
                {k.replace('_', ' ')}
              </label>
            ))}
            <input placeholder="Education topics (comma-separated)" disabled={!isDraft || busy}
              defaultValue={(iv.education || []).join(', ')}
              onBlur={e => isDraft && saveDraft({ interventions: { ...iv, education: e.target.value.split(',').map(x => x.trim()).filter(Boolean) } })}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', minWidth: 240, fontSize: 12 }} />
          </div>
        </div>

        {/* 6 · Totals */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <b>Totals</b>
          <input type="number" placeholder="Fills authorized" disabled={!isDraft || busy} defaultValue={latest.fills_authorized ?? ''}
            onBlur={e => isDraft && e.target.value !== '' && saveDraft({ fills_authorized: Number(e.target.value) })}
            style={{ width: 120, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }} />
          <input type="number" placeholder="Days supply" disabled={!isDraft || busy} defaultValue={latest.days_supply ?? ''}
            onBlur={e => isDraft && e.target.value !== '' && saveDraft({ days_supply: Number(e.target.value) })}
            style={{ width: 110, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }} />
        </div>

        {/* E1 (ASP-CRX-29): education needs + ADR history — assessment fields, frozen with the version */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <textarea placeholder="Education needs" disabled={!isDraft || busy} defaultValue={latest.education_needs ?? ''}
            onBlur={e => isDraft && saveDraft({ education_needs: e.target.value })}
            style={{ flex: 1, minWidth: 240, minHeight: 44, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }} />
          <textarea placeholder="ADR history" disabled={!isDraft || busy} defaultValue={latest.adr_history ?? ''}
            onBlur={e => isDraft && saveDraft({ adr_history: e.target.value })}
            style={{ flex: 1, minWidth: 240, minHeight: 44, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }} />
        </div>

        {/* E4 (ASP-CRX-30): the delta vs the immediately prior version — field-level changed/unchanged */}
        {data.previous && latest.version > 1 && (
          <div style={{ border: '1px dashed var(--color-border)', borderRadius: 8, padding: 8, fontSize: 12 }}>
            <b>Delta vs v{data.previous.version}</b>{' — '}
            {(() => {
              const fields: [string, any, any][] = [
                ['Problems', latest.problem_list, data.previous.problem_list],
                ['Interventions', latest.interventions, data.previous.interventions],
                ['Fills authorized', latest.fills_authorized, data.previous.fills_authorized],
                ['Days supply', latest.days_supply, data.previous.days_supply],
                ['Education needs', latest.education_needs, data.previous.education_needs],
                ['ADR history', latest.adr_history, data.previous.adr_history],
              ]
              const changed = fields.filter(([, a, b0]) => JSON.stringify(a ?? null) !== JSON.stringify(b0 ?? null))
              return changed.length === 0
                ? <span style={{ color: 'var(--color-text-mute)' }}>no field changes (goal/status activity may still differ)</span>
                : <span>changed: {changed.map(([k]) => k).join(' · ')}<span style={{ color: 'var(--color-text-mute)' }}> · unchanged: {fields.length - changed.length} fields</span></span>
            })()}
          </div>
        )}

        {/* 3 · Goals */}
        <div>
          <b>Goals</b>
          {(data.goals || []).map((g: any) => (
            <div key={g.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>[{g.horizon}] {g.goal_text}{g.measurable_target ? ` — ${g.measurable_target}` : ''}{g.target_date ? ` · by ${formatUsDate(g.target_date)}` : ''}</span>
              {(['not_met', 'progressing', 'met'] as const).map(st => (
                <button key={st} disabled={busy}
                  style={{ ...xbtn, fontWeight: g.status === st ? 700 : 400, color: g.status === st ? (st === 'met' ? '#15803d' : st === 'progressing' ? '#b45309' : '#b91c1c') : 'var(--color-text-mute)' }}
                  onClick={async () => { try { await api.updateCareRxCarePlanGoal(g.id, { status: st }); load() } catch (e: any) { setMsg(e?.body?.message || 'Goal update failed.') } }}>
                  {st.replace('_', ' ')}
                </button>
              ))}
            </div>
          ))}
          {isDraft && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              <SearchableSelect value={gHorizon} onChange={(v: string) => setGHorizon(v)}
                options={[{ value: 'short', label: 'Short (90d)' }, { value: 'long', label: 'Long (annual)' }]} />
              <input placeholder="Goal" value={gText} onChange={e => setGText(e.target.value)} style={inp} />
              <input placeholder="Measurable target" value={gTarget} onChange={e => setGTarget(e.target.value)} style={inp} />
              <DateInput label="Goal target date" value={gDate} onChange={v => setGDate(v ?? '')} style={inp} offsetHelper={{}} />
              <button disabled={busy || !gText.trim()} style={btn} onClick={async () => {
                try {
                  await api.addCareRxCarePlanGoal(latest.id, { horizon: gHorizon, goal_text: gText.trim(), measurable_target: gTarget || undefined, target_date: gDate || undefined })
                  setGText(''); setGTarget(''); setGDate(''); load()
                } catch (e: any) { setMsg(e?.body?.message || 'Goal add failed.') }
              }}>+ Goal</button>
            </div>
          )}
        </div>

        {/* AI advisory layer (ASP-CRX-16 / spec §A Stage 6 AI): every draft LABELED, acceptance is a
            pharmacist act (the accepted goal row carries the actor), never auto-sets/clears/closes.
            Rides the existing tokenized + audited carerx/ai/assist path — no new AI surface. */}
        <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b>AI (advisory only)</b>
            {isDraft && (
              <button disabled={aiBusy} style={btn} onClick={async () => {
                setAiBusy(true); setMsg('')
                try {
                  const r = await deidentifiedAiAssist(phiValues, [{ role: 'user', content:
                    `You draft specialty-pharmacy care-plan goals. Drug: ${drugName || 'unknown'}. ` +
                    `Existing problems: ${problems.map(pp => pp.code + ' ' + pp.description).join('; ') || 'none listed'}. ` +
                    `Respond ONLY with JSON: an array of 3-4 objects {"horizon":"short"|"long","goal_text":string,"measurable_target":string}. ` +
                    `Short = 90-day, long = annual. Goals must be measurable and drug-appropriate. No prose.` }], 700)
                  const clean = r.text.replace(/```json|```/g, '').trim()
                  const arr = JSON.parse(clean)
                  setAiDrafts(Array.isArray(arr) ? arr.filter((d: any) => d?.goal_text && (d.horizon === 'short' || d.horizon === 'long')) : [])
                } catch { setMsg('AI drafting unavailable — write goals manually.') } finally { setAiBusy(false) }
              }}>{aiBusy ? 'Drafting…' : '✨ Draft goals'}</button>
            )}
            <button disabled={aiBusy} style={btn} onClick={async () => {
              setAiBusy(true); setMsg('')
              try {
                const r = await deidentifiedAiAssist(phiValues, [{ role: 'user', content:
                  `Summarize this care-plan assessment for a pharmacist in 3 short sentences, plain text: ` +
                  `Drug ${drugName || 'unknown'}; snapshot ${latest.assessment || 'not yet frozen'}; ` +
                  `goals ${(data.goals || []).map((g: any) => `[${g.status}] ${g.goal_text}`).join('; ') || 'none'}.` }], 400)
                setAiSummary(r.text.trim())
              } catch { setMsg('AI summary unavailable.') } finally { setAiBusy(false) }
            }}>Summarize assessment</button>
          </div>
          {aiSummary && <div style={{ marginTop: 6, padding: 8, background: 'var(--color-bg)', borderRadius: 8 }}><i>AI summary (advisory):</i> {aiSummary}</div>}
          {aiDrafts && (
            <div style={{ marginTop: 6 }}>
              <i>AI goal drafts — advisory only; accepting records the goal as your act:</i>
              {aiDrafts.length === 0 && <div style={{ color: 'var(--color-text-mute)' }}>No usable drafts returned.</div>}
              {aiDrafts.map((d0, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                  <span>[{d0.horizon}] {d0.goal_text}{d0.measurable_target ? ` — ${d0.measurable_target}` : ''}</span>
                  <button disabled={busy || !isDraft} style={btn} onClick={async () => {
                    try {
                      await api.addCareRxCarePlanGoal(latest.id, { horizon: d0.horizon, goal_text: d0.goal_text, measurable_target: d0.measurable_target || undefined })
                      setAiDrafts(prev => prev ? prev.filter((_, j) => j !== i) : prev); load()
                    } catch (e: any) { setMsg(e?.body?.message || 'Accept failed.') }
                  }}>Accept</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ASP-CRX-31 (F5/F6): dose history — the cadence anchor. POT completion records doses;
            manual entry covers history/corrections. Dose 1 re-anchors open cadence server-side. */}
        <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b>Doses</b>
            {doses.length === 0 && <span style={{ color: 'var(--color-text-mute)' }}>none recorded</span>}
            {doses.map(d0 => <span key={d0.dose_no} title={d0.source}>#{d0.dose_no} {formatUsDate(d0.dosed_at)}</span>)}
            <DateInput label="Dose date" value={doseDate} onChange={v => setDoseDate(v ?? '')} />
            <button disabled={busy || !doseDate} style={btn} onClick={async () => {
              try {
                const r = await api.addCareRxDose(therapyId, doseDate)
                setDoseDate(''); setMsg(r.reanchored > 0 ? `Dose #${r.dose_no} recorded — ${r.reanchored} cadence tasks re-anchored to it.` : `Dose #${r.dose_no} recorded.`)
                load()
              } catch (e: any) { setMsg(e?.body?.message || 'Dose record failed.') }
            }}>Record dose</button>
          </div>
        </div>

        {/* 5 · Cadence */}
        {(data.cadence || []).length > 0 && (
          <div>
            <b>Follow-up schedule</b>
            {(data.cadence || []).map((t: any) => (
              <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: t.status === 'open' && t.due_at && new Date(t.due_at) < new Date() ? '#b91c1c' : undefined }}>
                  {String(t.code).replace(/_/g, ' ')} ({String(t.owner_role).toUpperCase()}) · {t.due_at ? formatUsDate(t.due_at) : '—'} · {t.status}
                </span>
                {t.status === 'open' && (<>
                  <button disabled={busy} style={xbtn} onClick={async () => {
                    try { await api.updateCareRxCadenceTask(t.id, { status: 'done' }); load() }
                    catch (e: any) { setMsg(e?.body?.message || 'Task update failed.') }
                  }}>done</button>
                  <button disabled={busy} style={xbtn} onClick={async () => {
                    const n = window.prompt('Skip reason:'); if (!n?.trim()) return
                    try { await api.updateCareRxCadenceTask(t.id, { status: 'skipped', notes: n.trim() }); load() }
                    catch (e: any) { setMsg(e?.body?.message || 'Task update failed.') }
                  }}>skip</button>
                </>)}
              </div>
            ))}
          </div>
        )}
      </>)}
    </div>
  )
}
const btn: React.CSSProperties = { fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 7, padding: '3px 10px', background: 'transparent', cursor: 'pointer', marginLeft: 6 }
const xbtn: React.CSSProperties = { fontSize: 11, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)' }
const inp: React.CSSProperties = { fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--color-border)' }
